import React, { useEffect, useState } from 'react';
import { Item, AccountDetail } from '../types';
import { getItems, getAccountDetails, syncItemsFromAccount, syncItemsFromAccounts, ItemSyncBatchResult, ItemSyncProgress, updateItem, deleteItem, updateItemMultiSpec, updateItemMultiQty } from '../services/api';
import { Box, RefreshCw, ShoppingBag, Edit, Trash2, Save, X } from 'lucide-react';

const ItemList: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [syncProgress, setSyncProgress] = useState<ItemSyncProgress | null>(null);
  const [syncSummary, setSyncSummary] = useState<ItemSyncBatchResult | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [editForm, setEditForm] = useState<Partial<Item>>({});

  const formatPrice = (value?: string | number) => {
    const text = String(value ?? '').trim();
    if (!text) return '价格未知';
    if (/^[¥￥]/.test(text)) return text;
    return `¥${text}`;
  };

  useEffect(() => {
    getAccountDetails().then(setAccounts);
    getItems().then(setItems);
  }, []);

  const handleSync = async () => {
      if (!selectedAccount) return alert('请先选择账号');
      setLoading(true);
      setSyncProgress(null);
      setSyncSummary(null);
      try {
        const result = await syncItemsFromAccount(selectedAccount);
        if (result?.success === false) throw new Error(result.message || '同步商品失败');
        setItems(await getItems());
      } catch (error) {
        console.error('同步商品失败:', error);
        alert(error instanceof Error ? error.message : '同步商品失败，请重试');
      } finally {
        setLoading(false);
      }
  };

  const handleSyncAll = async () => {
    if (accounts.length === 0) {
      setSyncSummary({ total: 0, successCount: 0, failedCount: 0, failedAccounts: [] });
      return;
    }

    setLoading(true);
    setSyncSummary(null);
    setSyncProgress({
      completed: 0,
      total: accounts.length,
      currentAccountId: null,
      successCount: 0,
      failedCount: 0,
    });

    try {
      const result = await syncItemsFromAccounts(
        accounts.map(account => account.id),
        setSyncProgress,
      );
      setSyncSummary(result);
      setItems(await getItems());
    } catch (error) {
      console.error('同步全部账号失败:', error);
      alert(error instanceof Error ? error.message : '同步全部账号失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: Item) => {
    setSelectedItem(item);
    setEditForm({ ...item });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedItem) return;
    try {
      await updateItem(selectedItem.cookie_id, selectedItem.item_id, {
        item_title: editForm.item_title,
        item_description: editForm.item_description,
        item_category: editForm.item_category,
        item_price: editForm.item_price,
        item_detail: editForm.item_detail,
      });
      const updatedItems = items.map(item =>
        item.cookie_id === selectedItem.cookie_id && item.item_id === selectedItem.item_id
          ? { ...item, ...editForm }
          : item
      );
      setItems(updatedItems);
      setShowEditModal(false);
    } catch (error) {
      console.error('更新商品失败:', error);
      alert('更新失败，请重试');
    }
  };

  const handleDelete = async (item: Item) => {
    if (confirm(`确认删除商品"${item.item_title}"吗？`)) {
      try {
        await deleteItem(item.cookie_id, item.item_id);
        const filteredItems = items.filter(i =>
          !(i.cookie_id === item.cookie_id && i.item_id === item.item_id)
        );
        setItems(filteredItems);
      } catch (error) {
        console.error('删除商品失败:', error);
        alert('删除失败，请重试');
      }
    }
  };

  const toggleMultiSpec = async (item: Item) => {
    try {
      const enabled = !item.is_multi_spec;
      await updateItemMultiSpec(item.cookie_id, item.item_id, Boolean(enabled));
      const updatedItems = items.map(i =>
        i.cookie_id === item.cookie_id && i.item_id === item.item_id
          ? { ...i, is_multi_spec: !i.is_multi_spec }
          : i
      );
      setItems(updatedItems);
    } catch (error) {
      console.error('切换状态失败:', error);
    }
  };

  const toggleMultiQty = async (item: Item) => {
    try {
      const enabled = !item.is_multi_qty_ship;
      await updateItemMultiQty(item.cookie_id, item.item_id, Boolean(enabled));
      const updatedItems = items.map(i =>
        i.cookie_id === item.cookie_id && i.item_id === item.item_id
          ? { ...i, is_multi_qty_ship: !i.is_multi_qty_ship }
          : i
      );
      setItems(updatedItems);
    } catch (error) {
      console.error('切换状态失败:', error);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">商品管理</h2>
          <p className="text-gray-500 mt-2 text-sm">监控并管理所有账号下的闲鱼商品。</p>
        </div>
        <div className="flex flex-wrap justify-end gap-3">
            <select
                className="ios-input px-4 py-3 rounded-xl text-sm"
                value={selectedAccount}
                onChange={e => setSelectedAccount(e.target.value)}
            >
                <option value="">选择账号以同步</option>
                {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.nickname}</option>
                ))}
            </select>
            <button
                onClick={handleSync}
                disabled={loading || !selectedAccount}
                className="ios-btn-primary flex items-center gap-2 px-6 py-3 rounded-2xl font-bold shadow-lg shadow-yellow-200 disabled:opacity-50"
            >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                同步商品
            </button>
            <button
                onClick={handleSyncAll}
                disabled={loading || accounts.length === 0}
                className="ios-btn-secondary flex items-center gap-2 px-5 py-3 rounded-2xl font-bold disabled:opacity-50"
                title="按顺序同步全部账号"
            >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                同步全部账号
            </button>
        </div>
      </div>

      {syncProgress && (
        <div className="ios-card p-4 rounded-2xl" role="status" aria-live="polite">
          <div className="flex items-center justify-between text-sm font-semibold text-gray-700">
            <span>同步进度 {syncProgress.completed}/{syncProgress.total}</span>
            <span>{syncProgress.total ? Math.round(syncProgress.completed / syncProgress.total * 100) : 100}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-[#FFE815] transition-all duration-300"
              style={{ width: `${syncProgress.total ? syncProgress.completed / syncProgress.total * 100 : 100}%` }}
            />
          </div>
          {syncProgress.currentAccountId && (
            <p className="mt-2 text-xs text-gray-500">
              正在同步：{accounts.find(account => account.id === syncProgress.currentAccountId)?.nickname || syncProgress.currentAccountId}
            </p>
          )}
        </div>
      )}

      {syncSummary && (
        <div className={`rounded-2xl border p-4 text-sm ${syncSummary.failedCount ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`} role="status" aria-live="polite">
          <p className="font-semibold">
            同步完成：成功 {syncSummary.successCount} 个，失败 {syncSummary.failedCount} 个
          </p>
          {syncSummary.failedAccounts.length > 0 && (
            <p className="mt-1 text-xs">
              失败账号：{syncSummary.failedAccounts.map(failed => accounts.find(account => account.id === failed.cookieId)?.nickname || failed.cookieId).join('、')}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map(item => (
              <div key={`${item.cookie_id}-${item.item_id}`} className="ios-card p-4 rounded-3xl hover:shadow-lg transition-all group relative">
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-[#FFE815] transition-colors"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-red-100 text-red-500 transition-colors"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                  </div>
                  <div className="aspect-square bg-gray-100 rounded-2xl mb-4 overflow-hidden relative">
                      {item.item_image ? (
                          <img src={item.item_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                              <Box className="w-10 h-10" />
                          </div>
                      )}
                      <div className="absolute top-2 left-2 bg-black/50 backdrop-blur-md text-white text-xs font-bold px-2 py-1 rounded-lg">
                          {formatPrice(item.item_price)}
                      </div>
                  </div>
                  <h3 className="font-bold text-gray-900 line-clamp-2 text-sm mb-2 h-10">{item.item_title}</h3>
                  <div className="flex justify-between items-center text-xs text-gray-500 mb-2">
                      <span className="bg-gray-100 px-2 py-1 rounded-md truncate max-w-[100px]">ID: {item.item_id}</span>
                  </div>
                  <div className="flex gap-2">
                      <button
                        onClick={() => toggleMultiSpec(item)}
                        className={`flex-1 text-xs font-bold px-2 py-1.5 rounded-lg transition-colors ${
                          item.is_multi_spec
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        多规格
                      </button>
                      <button
                        onClick={() => toggleMultiQty(item)}
                        className={`flex-1 text-xs font-bold px-2 py-1.5 rounded-lg transition-colors ${
                          item.is_multi_qty_ship
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        多数量发货
                      </button>
                  </div>
              </div>
          ))}
          {items.length === 0 && (
             <div className="col-span-full py-20 text-center text-gray-400">
                 <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-30" />
                 暂无商品数据，请选择账号进行同步
             </div>
          )}
      </div>

      {showEditModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">编辑商品</h3>
              <button onClick={() => setShowEditModal(false)} className="rounded-lg p-2 hover:bg-gray-100" title="关闭">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">标题
                <input className="ios-input mt-1 w-full" value={editForm.item_title || ''} onChange={e => setEditForm({...editForm, item_title: e.target.value})} />
              </label>
              <label className="block text-sm font-medium text-gray-700">价格
                <input className="ios-input mt-1 w-full" value={editForm.item_price || ''} onChange={e => setEditForm({...editForm, item_price: e.target.value})} />
              </label>
              <label className="block text-sm font-medium text-gray-700">描述
                <textarea className="ios-input mt-1 min-h-24 w-full" value={editForm.item_description || ''} onChange={e => setEditForm({...editForm, item_description: e.target.value})} />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowEditModal(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-gray-100">取消</button>
              <button onClick={handleSaveEdit} className="ios-btn-primary flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold"><Save className="h-4 w-4" />保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemList;
